// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState, useRef } from 'react';
import omit from 'lodash/omit';
import clone from 'fast-copy';
import { useContextSelector } from 'use-context-selector';
import { stringify } from 'query-string';
import { dequal } from 'dequal';

import Filters from 'Components/Filters/Filters';
import Brands from 'Containers/Brands/Brands';
import Categories from 'Containers/Categories/Categories';
import useFilters from 'Hooks/useFilters';
import FilterGroup from 'Containers/Filters/FilterGroup';
import ActiveFilters from 'Containers/Filters/ActiveFilters';
import AvailabilityFilters from 'Containers/Filters/AvailabilityFilters';
import PriceFilter from 'Containers/Filters/PriceFilter';
import useCategories from 'Hooks/useCategories';
import { ProductsContext } from 'Contexts/ProductsContext';
import FilterSwitch from 'Components/Filters/FilterSwitch';
import useRouter from 'Hooks/useRouter';
import useScreen from 'Hooks/useScreen';
import useAnalytics from 'Hooks/useAnalytics';
import useScrollLock from 'Hooks/useScrollLock';
import FiltersHeaderMobile from 'Components/Filters/FiltersHeaderMobile';

const canUpdateIfMulti = ['price', 'category', 'search'];

const FiltersContainer = memo(() => {
  const { query, location, push } = useRouter();
  const { data: filters, clearFilters, activeFilters = {}, updateFilters, mobileFiltersOpen, setMobileFiltersOpen } = useFilters();
  const { data: categories, categoryHistory, activeCategory } = useCategories();
  const isLoading = useContextSelector(ProductsContext, ({ isLoading: loading }) => loading);
  const [localFilterState, setFilterState] = useState({ ...activeFilters });
  const screen = useScreen();
  const analytics = useAnalytics();
  const prevFiltersRef = useRef();

  useEffect(() => {
    const filterKeys = Object.keys(localFilterState);
    const searchedValues = filterKeys.reduce((acc, key) => {
      const value = localFilterState[key];

      if (!value) return acc;

      if (key === 'category') {
        if (value && Object.keys(value).length > 0) {
          acc[key] = value.slug;
        }
      } else if (key === 'price') {
        acc[key] = value.join(', ');
      } else if (key === 'search') {
        acc[key] = value.join(', ');
      } else if (Array.isArray(value)) {
        acc[key] = value.map(item => item.name || item.id || item.valueId).join(', ');
      } else {
        acc[key] = value.name || value.id;
      }

      const specialFlags = [
        'only_promotional',
        'only_resealed',
        'only_warehouse_products',
        'eol',
        'only_available',
        'include_reserved_stock',
        'stock-clearing-sale'
      ];
      const queryFilters = specialFlags.filter(flag => query[flag] === 1 || query[flag] === '1');
      if (queryFilters.length > 0) {
        acc.query = queryFilters.join(', ');
      }
      return acc;
    }, {});

    // Check if the filters have changed
    if (
      Object.keys(searchedValues).length > 0
      && !dequal(prevFiltersRef.current, searchedValues)
    ) {
      analytics.track('filters_applied', { filters: searchedValues });
      prevFiltersRef.current = searchedValues;
    }
  }, [localFilterState]);

  useEffect(() => {
    setFilterState({ ...activeFilters });
  }, [activeFilters]);

  useScrollLock(mobileFiltersOpen);

  const handleOnClear = () => {
    clearFilters();
  };

  const handleOnFilterChange = (key, value) => (checked, force = false) => {
    const hasFilter = localFilterState && localFilterState[key];
    const defaultValue = Array.isArray(value) ? [] : false;
    const payload = {
      [key]: hasFilter
        ? clone(localFilterState[key])
        : defaultValue
    };

    if (!value) {
      if (
        query.filtering !== 'multi'
        || canUpdateIfMulti.includes(key)
      ) {
        updateFilters({ [key]: value });
      } else {
        setFilterState(prevState => ({
          ...prevState,
          [key]: value
        }));
      }
      return;
    }

    if (checked) {
      if (Array.isArray(value)) {
        payload[key].push(...value);
      } else {
        payload[key] = value;
      }
      setFilterState(prevState => ({
        ...prevState,
        [key]: payload[key]
      }));
    } else if (Array.isArray(value)) {
      if (dequal(payload[key], value)) {
        payload[key] = [];
      } else {
        // eslint-disable-next-line no-restricted-syntax
        for (const filterItem of value) {
          const idx = payload[key]
            .findIndex(i => (i.id || i.valueId) === (filterItem.id || filterItem.valueId));
          if (idx !== -1) {
            payload[key].splice(idx, 1);
          }
        }
      }

      setFilterState(prevState => ({
        ...prevState,
        [key]: payload[key]
      }));
    }

    if (
      query.filtering !== 'multi'
      || canUpdateIfMulti.includes(key)
      || force === true
    ) {
      updateFilters(payload);
    }
  };

  const handleOnSwitch = instant => {
    analytics.track('filters_switch', { type: instant ? 'instant' : 'multi' });
    push({
      pathname: location.pathname,
      search: stringify({ ...query, filtering: instant ? 'instant' : 'multi' })
    });
  };

  const handleOnApply = () => {
    analytics.track('multi_filters_applied', { filters: localFilterState });
    updateFilters(localFilterState);
  };

  const handleOnPriceChange = price => {
    updateFilters(price);
  };

  // const handleOnStockToggle = checked => {
  //   if (checked) {
  //     if (!query.only_available) {
  //       push({ search: stringify({ ...query, only_available: 1 }) });
  //     }
  //   } else if (query.only_available) {
  //     delete query.only_available;
  //     push({ search: stringify(query) });
  //   }
  //   updateFilters({
  //     ...localFilterState,
  //     only_available: checked
  //   });
  // };
  const prevCat = categoryHistory.at(-2) || categoryHistory.at(-1);
  let categoriesToDisplay = filters?.categories;
  if (
    !activeCategory
    // || (
    //   !filters?.categories?.find(cat => cat.id === activeCategory.id)
    //   && !filters?.categories?.every(v => activeCategory?.children?.find(cat => cat.id === v.id))
    // )
    || (
      categories.find(cat => cat.id === activeCategory.id)
      && isLoading
      && !activeCategory.children.find(cat => cat.id === prevCat?.id)
    )
  ) {
    categoriesToDisplay = categories;
  }
  // if (activeCategory) {
  //   if (activeCategoryParts.length === 1) {
  //     if (
  //       !isLoading
  //       || (
  //         categories.find(i => i.id === activeCategory.id)
  //         && (filters?.categories || []).every(cat => (activeCategory.children || []).find(c => c.id === cat.id))
  //       )
  //     ) {
  //       categoriesToDisplay = filters?.categories;
  //     }
  //   } else {
  //     categoriesToDisplay = filters?.categories;
  //   }
  // }
  return (
    <Filters
      filters={ filters?.properties || [] }
      activeFilters={ localFilterState }
      mobileFiltersOpen={ mobileFiltersOpen }
    >
      <FiltersHeaderMobile
        activeFilters={ omit(activeFilters, ['category']) }
        setMobileFiltersOpen={ setMobileFiltersOpen }
        clearFilters={ handleOnClear }
      >
        <ActiveFilters
          activeFilters={ omit(activeFilters, ['category']) }
          onChange={ handleOnFilterChange }
          onClear={ handleOnClear }
        />
      </FiltersHeaderMobile>
      { (filters?.categories || []).length > 0 && (
        <Categories
          categories={ categoriesToDisplay }
          selected={ localFilterState?.category }
          onChange={ handleOnFilterChange }
        />
      ) }
      <FilterSwitch
        onSwitch={ handleOnSwitch }
        onApply={ handleOnApply }
        filterType={ query.filtering }
      />
      { screen.width > 767 && (
        <ActiveFilters
          activeFilters={ omit(activeFilters, ['category']) }
          onChange={ handleOnFilterChange }
          onClear={ handleOnClear }
        />
      ) }
      <AvailabilityFilters
        onChange={ handleOnFilterChange }
      />
      <PriceFilter
        onChange={ handleOnPriceChange }
        options={{
          // todo: get from api
          groups: [
            [0, 500],
            [500, 1000],
            [1000, 1500],
            [1500, 2000],
            [2000, 3000],
            [3000, 4000],
            [4000, 5000],
            [5000, 100000]
          ],
          min: 0,
          max: 100000
        }}
        selected={ localFilterState?.price || [] }
      />
      <Brands
        brands={ filters?.manufacturers || [] }
        selected={ localFilterState?.brands }
        onChange={ handleOnFilterChange }
      />
      { filters?.properties?.map(filter => (
        <FilterGroup
          key={ `${filter.nameId}_${filter.slug}` }
          filter={ filter }
          onChange={ handleOnFilterChange }
          activeFilter={ localFilterState && localFilterState[filter.slug] }
        />
      )) }
    </Filters>
  );
});

FiltersContainer.displayName = 'FiltersContainer';

export default FiltersContainer;
